import { parseUserSetting, useUserSettings } from '../profile/utils';
import { useSetUserSettingMutation } from '../profile/queryHooks';
import {
  BREAK_DISPLAY_DISABLED,
  BREAK_DISPLAY_DISMISSED_BREAK,
  UserSetting,
} from '../profile/types';

const selectDismissedBreak = (settings: UserSetting) =>
  parseUserSetting<string>(settings[BREAK_DISPLAY_DISMISSED_BREAK], '');

const selectBreakDisplayDisabled = (settings: UserSetting) =>
  parseUserSetting<boolean>(settings[BREAK_DISPLAY_DISABLED], false);

/**
 * The user's break-display settings, read straight from the settings query so they stay live.
 *
 * These are deliberately not read through `usePersistedState`: the overlay is mounted once for
 * the whole session, and `usePersistedState` snapshots its value on first render. A snapshot
 * would leave the overlay hidden after the user clears these on the settings page, until they
 * reloaded the browser.
 */
export const useBreakDisplayUserSettings = () => {
  const { mutate: setUserSetting } = useSetUserSettingMutation();

  return {
    dismissedBreak: useUserSettings(selectDismissedBreak),
    disabled: useUserSettings(selectBreakDisplayDisabled),
    /** Records this break as dismissed, so a reload during it does not re-show the announcement. */
    dismissBreak: (breakKey: string) =>
      setUserSetting({
        key: BREAK_DISPLAY_DISMISSED_BREAK,
        value: JSON.stringify(breakKey),
      }),
    /** Opts the user out of break announcements until they reset it on the settings page. */
    disableBreakDisplay: () =>
      setUserSetting({
        key: BREAK_DISPLAY_DISABLED,
        value: JSON.stringify(true),
      }),
  };
};
